import { CoreBumpRecommendationListingCard, Core_Bump_Recommendation_ListingQuery } from '@reverbdotcom/commons/src/gql/graphql';

export const DELETE_BID_AMOUNT = 0.0;

export const BRAND_NEW_CONDITION_SLUGS = Object.freeze([
  'brand-new',
]);

export const USED_CONDITION_SLUGS = Object.freeze([
  'non-functioning',
  'poor',
  'fair',
  'good',
  'very-good',
  'excellent',
  'mint',
  'mint-inventory',
  'b-stock',
]);

export function isBrandNew(conditionSlug: string | null | undefined) {
  return BRAND_NEW_CONDITION_SLUGS.indexOf(conditionSlug ?? '') > -1;
}

export function isCSPMatched(listing: CoreBumpRecommendationListingCard.Fragment) {
  return !!listing.csp?.id;
}

// handle annoying JS math float weirdness resulting from things like:
// 0.07 * 100
// 7.000000000000001
// This will instead now return 7
export function fixPrecision(amount: number): number {
  return Number.parseFloat((amount).toFixed(1));
}

// converts a decimal representation of a "bid"
// to a percent:
// 0.075 returns 7.5
export function formatBid(bid: number): number {
  return fixPrecision(bid * 100);
}

// converts a percent representation of a "bump rate"
// to a "bid" decimal:
// 7.5 returns 0.075
export function bumpRatePercentToDecimal(bumpRate: number): number {
  return bumpRate / 100;
}

export function getCompetitorCount(listing: {
  condition?: { conditionSlug?: string | null } | null;
  csp?: { inventory?: { newTotal?: number | null; usedTotal?: number | null } | null } | null;
}): number {
  if (isBrandNew(listing.condition?.conditionSlug)) {
    return Math.max((listing?.csp?.inventory?.newTotal ?? 0) - 1, 0);
  }

  return Math.max((listing?.csp?.inventory?.usedTotal ?? 0) - 1, 0);
}

export function getInsight(listing: Core_Bump_Recommendation_ListingQuery['listing']) {
  return listing?.featuredListingInsight ?? listing?.featuredListingInsightPreview;
}

export type FeedbackStateKey =
  | 'noBump'
  | 'willIncreaseVisibility'
  | 'visibilityIsIncreased'
  | 'currentlyWinningTopPickNoBump'
  | 'currentlyWinningTopPickUnderRecommendation'
  | 'currentlyWinningTopPick'
  | 'currentlyWinningTopPickOverbid'
  | 'willWinTopPick'
  | 'willWinTopPickOverbid';

export interface FeedbackState {
  key: FeedbackStateKey;
  winner: boolean;
  overbid: boolean;
}

// Picks the feedback message shown under the bump rate selector. Each key maps
// to a complete sentence in discovery.bumpModal.feedbackCallout/feedbackDetails.
export function getFeedbackState({
  currentBidPercent,
  selectedBidPercent,
  recommendedBidPercent,
  isCurrentFeaturedWinner,
}: {
  currentBidPercent: number;
  selectedBidPercent: number;
  recommendedBidPercent: number;
  isCurrentFeaturedWinner?: boolean | null;
}): FeedbackState {
  const meetsRecommendation = selectedBidPercent >= recommendedBidPercent;
  const overbid = selectedBidPercent > recommendedBidPercent;
  // A featured winner keeps the top pick as long as they don't lower their bid,
  // even when the recommendation has since climbed above it.
  const keepsWinningBid = !!isCurrentFeaturedWinner && selectedBidPercent >= currentBidPercent;
  const winner = meetsRecommendation || keepsWinningBid;
  const selectionUnchanged = selectedBidPercent === currentBidPercent;
  const hasExistingBump = currentBidPercent > 0;

  if (!winner) {
    if (selectedBidPercent === 0) {
      return { key: 'noBump', winner, overbid };
    }
    return { key: selectionUnchanged ? 'visibilityIsIncreased' : 'willIncreaseVisibility', winner, overbid };
  }

  if (isCurrentFeaturedWinner) {
    if (!hasExistingBump && !overbid) {
      return { key: 'currentlyWinningTopPickNoBump', winner, overbid };
    }

    if (hasExistingBump && !meetsRecommendation) {
      return { key: 'currentlyWinningTopPickUnderRecommendation', winner, overbid };
    }

    if (selectionUnchanged) {
      return { key: overbid ? 'currentlyWinningTopPickOverbid' : 'currentlyWinningTopPick', winner, overbid };
    }
  }

  return { key: overbid ? 'willWinTopPickOverbid' : 'willWinTopPick', winner, overbid };
}

export function recommendationAmount(
  listing: Core_Bump_Recommendation_ListingQuery['listing'], showTopPickVariant = false,
): number | null {
  if (showTopPickVariant) {
    const insight = getInsight(listing);
    if (insight?.recommendedBumpRate != null) {
      return insight.recommendedBumpRate;
    }
  }
  if (!listing?.bumpRecommendations) return null;
  return fixPrecision(listing.bumpRecommendations.bumpRecommendationAmount);
}
